import { useParams } from 'react-router-dom';
import { ShowSection } from 'framework/ui/layout';
import {
  CHANGE_REQUEST_STATUS,
  useProjectFinanceService,
  useNewProjectService,
  useNewProjectUrl,
} from 'domain/shared/newProject';
import ChangeRequestForm from '../ChangeRequestForm';
import ChangeRequestActionButtons from '../ChangeRequestActionButtons';
import { UpdateForm } from 'framework/form';
import { removeGuidelinesFromContent } from '../Create/CreateChangeRequestLayout';
import useChangeRequestService from 'domain/projects/newProject/finance/changeRequests/changeRequestService';
import useMilestonesService from 'domain/projects/newProject/milestones/useMilestonesService';
import useBillingService from 'domain/projects/newProject/finance/billing/billingService';

const UpdateChangeRequestLayout = () => {
  const { milestonesSearch, milestonesBatch } = useMilestonesService();

  const { searchSensitiveProjectInfo, saveSensitiveProjectInfo, getBudget } = useProjectFinanceService();
  const { updateChangeRequest, getChangeRequestById, getChangeRequestValidationSchema } = useChangeRequestService();
  const { getFinanceUrl } = useNewProjectUrl();
  const { billingScheduleSearch } = useBillingService();
  const { getById } = useNewProjectService();

  const { id, requestId } = useParams();

  return (
    <UpdateForm
      idSource="requestId"
      getDataFunc={async () => {
        const [milestones, changeRequest, billingScheduleResponse] = await Promise.all([
          milestonesSearch(id!, {
            filter: {
              deletedAt: {
                null: true,
              },
            },
          }),
          getChangeRequestById(requestId!),
          billingScheduleSearch({
            pageSize: 100,
            filter: { projectId: { eq: id! } },
          }).catch(() => ({ result: [] })),
        ]);

        const financeAmount = await searchSensitiveProjectInfo([changeRequest.additionalBudgetAmountId]).then(
          response => response?.[0]?.value,
        );

        const billingSchedule = billingScheduleResponse?.result?.[0];
        let billingType: string | null = billingSchedule?.billingType;

        if (!billingType) {
          try {
            const projectData = await getById(id!);
            const budgetResponse = projectData?.defaultBudgetId
              ? await getBudget(projectData.defaultBudgetId).catch(() => null)
              : null;
            billingType = budgetResponse?.billingType || null;
          } catch {}
        }

        return {
          ...changeRequest,
          additionalBudgetAmount: financeAmount,
          milestones: milestones.result,
          milestoneSchedule: false,
          billingType,
        };
      }}
      submitFormFunc={async (reqId, values) => {
        const createdAdditionalBudgetAmountId = values.additionalBudgetAmount
          ? await saveSensitiveProjectInfo(values.additionalBudgetAmount).then(response => response.id)
          : null;
        const cleanRawContent = removeGuidelinesFromContent(values.rawContent);

        return Promise.all([
          ...(values.milestoneSchedule ? [milestonesBatch(id!, values.milestones)] : []),
          updateChangeRequest(reqId, {
            title: values.title,
            status: values.isDraft ? CHANGE_REQUEST_STATUS.DRAFT : CHANGE_REQUEST_STATUS.IN_APPROVAL,
            additionalHours: Number(values.additionalHours),
            additionalFte: values.additionalFte ? Number(values.additionalFte) : null,
            additionalBudgetAmountId: createdAdditionalBudgetAmountId,
            receiverFullName: values.receiverFullName,
            receiverEmail: values.receiverEmail,
            receiverTitle: values.receiverTitle,
            newDeliveryDate: values.newDeliveryDate,
            billingType: values.billingType,
            billingMilestoneId: values.billingMilestoneId,
            rawContent: values.isDraft ? values.rawContent : cleanRawContent,
            companyName: values.companyName,
          }),
        ]);
      }}
      afterSubmit={{
        redirect: getFinanceUrl(id),
        message: 'You are awesome! Change request has been successfully updated.',
      }}
      getValidationSchemaFunc={getChangeRequestValidationSchema}
    >
      <ShowSection title="Update Change Request:" titleVariant="h2">
        <ChangeRequestForm projectId={id} />
        <ChangeRequestActionButtons />
      </ShowSection>
    </UpdateForm>
  );
};

export default UpdateChangeRequestLayout;
